import React, { useEffect, useState } from "react";
import { fetchSiteTypes, createSiteType, deleteSiteType, fetchSites, createSite, deleteSite, updateSite } from "./services/api";
import { PMSite, PMSiteType } from "./types";
import { Button, Col, Form, Row } from "react-bootstrap";

const PMSitePage: React.FC = () => {
    const [sites, setSites] = useState<PMSite[]>([]);
    const [siteTypes, setSiteTypes] = useState<PMSiteType[]>([]);
    const [newSite, setNewSite] = useState<{
        site_name: string;
        site_type: number;
        location: string;
    }>({
        site_name: "",
        site_type: 0,
        location: ""
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<number>(0);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState({ site_name: '', site_type: 0, location: '' });

    useEffect(() => {
        getSiteTypes();
        getSites();
    }, []);

    const getSiteTypes = async () => {
        const res = await fetchSiteTypes();
        setSiteTypes(res);
    };

    const getSites = async () => {
        const res = await fetchSites();
        setSites(res);
    };

    const addSite = async () => {
        if (!newSite.site_name || !newSite.site_type || !newSite.location) return;
        await createSite(newSite);
        setNewSite({
            site_name: "",
            site_type: 0,
            location: ""
        });
        getSites();
    };

    const handleDeleteSite = async (id?: number) => {
        if (!id) return;
        await deleteSite(id);
        getSites();
    };

    const startEdit = (site: PMSite) => {
        setEditingId(site.id!);
        setEditForm({ site_name: site.site_name, site_type: site.site_type, location: site.location });
    };

    const saveEdit = async (id: number) => {
        await updateSite(id, editForm);
        setEditingId(null);
        getSites();
    };

    const cancelEdit = () => {
        setEditingId(null);
    };

    const filteredSites = sites.filter(site =>
        (filterType === 0 || site.site_type === filterType) &&
        (site.site_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            site.location.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="p-4 p-md-5">
            <h2 className="mb-4">Pan India Site Master</h2>
            <Form className="mb-3" style={{ border: "1px solid", padding: "15px", borderRadius: "5px" }}>
                <Row className="g-2 align-items-center">
                    <Col md={3}>
                        <Form.Group className="mb-4">
                            <Form.Label>Site Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter site name"
                                value={newSite.site_name}
                                onChange={e => setNewSite({ ...newSite, site_name: e.target.value })}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-4">
                            <Form.Label>Site Type</Form.Label>
                            <Form.Select
                                value={newSite.site_type}
                                onChange={e => setNewSite({ ...newSite, site_type: parseInt(e.target.value) })}
                            >
                                <option value={0}>Select Site Type</option>
                                {siteTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.type_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-4">
                            <Form.Label>Location</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter location"
                                value={newSite.location}
                                onChange={e => setNewSite({ ...newSite, location: e.target.value })}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Button className="btn btn-primary" onClick={addSite}>
                            Add Site
                        </Button>
                    </Col>
                </Row>
            </Form>

            {/* Search and Filter */}
            <Form className="mb-4">
                <Row className="g-2 align-items-center">
                    <Col md={4}>
                        <Form.Group className="mb-4">
                            <Form.Label>Search</Form.Label>
                            <Form.Control
                                type="search"
                                placeholder="Search name or location"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-4">
                            <Form.Label>Filter by Site Type</Form.Label>
                            <Form.Select
                                value={filterType}
                                onChange={e => setFilterType(parseInt(e.target.value))}
                            >
                                <option value={0}>All</option>
                                {siteTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.type_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            </Form>

            {/* Sites Table */}
            <div className="row">
                <div className="col-md-12">
                    <table className="table table-bordered align-middle">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: "25%" }}>Site Name</th>
                                <th style={{ width: "20%" }}>Site Type</th>
                                <th style={{ width: "30%" }}>Location</th>
                                <th style={{ width: "17%" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSites.length > 0 ? (
                                filteredSites.map((site) => (
                                    <tr key={site.id}>
                                        {editingId === site.id ? (
                                            <>
                                                <td>
                                                    <Form.Control value={editForm.site_name} onChange={e => setEditForm({ ...editForm, site_name: e.target.value })} />
                                                </td>
                                                <td>
                                                    <Form.Select value={editForm.site_type} onChange={e => setEditForm({ ...editForm, site_type: parseInt(e.target.value) })}>
                                                        <option value={0}>Select</option>
                                                        {siteTypes.map(t => <option key={t.id} value={t.id}>{t.type_name}</option>)}
                                                    </Form.Select>
                                                </td>
                                                <td>
                                                    <Form.Control value={editForm.location} onChange={e => setEditForm({ ...editForm, location: e.target.value })} />
                                                </td>
                                                <td>
                                                    <button className="btn btn-sm btn-success" onClick={() => saveEdit(site.id!)}>Save</button>
                                                    <button className="btn btn-sm btn-secondary" onClick={cancelEdit}>Cancel</button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td>{site.site_name}</td>
                                                <td>
                                                    {siteTypes.find(type => type.id === site.site_type)?.type_name || 'Unknown'}
                                                </td>
                                                <td>{site.location}</td>
                                                <td>
                                                    <Button
                                                        variant="warning"
                                                        size="sm"
                                                        onClick={() => startEdit(site)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDeleteSite(site.id)}
                                                        className="ms-2"
                                                    >
                                                        Delete
                                                    </Button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="text-center">
                                        No sites found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PMSitePage;
